import React, { useCallback, useState } from 'react';
import { FaTv, FaUsers, FaTh, FaCalendarAlt, FaMosque, FaSignOutAlt, FaComment, FaBars, FaTimes, FaBook, FaHandHoldingHeart } from "react-icons/fa";
import { NavLink } from 'react-router-dom';
import Modal from './Modal';
import MobileBar from './MobileBar';
import '../styles/Sidebar.css';

/**
 * Sidebar v2 (safe/defensive)
 * - Avoids prop name "toggle" to prevent collisions with external stores/hooks
 * - Supports controlled and uncontrolled open state
 *   Controlled:  pass isOpen + onToggle
 *   Uncontrolled: omit both, Sidebar manages its own open state
 */
function Sidebar({
    children,
    isOpen: controlledOpen,
    onToggle,
    isMobile,
    onLogout,
    kempenMode
}) {
    const isControlled = typeof controlledOpen === 'boolean' && typeof onToggle === 'function';
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

    const handleToggle = useCallback(() => {
        if (isControlled) {
            onToggle();
        } else {
            setUncontrolledOpen(v => !v);
        }
    }, [isControlled, onToggle]);

    const [showLogoutPopup, setShowLogoutPopup] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    const commonMenuItems = [
        { path: "/dashboard", name: "Dashboard", icon: <FaTh /> },
        { path: "/utama", name: "Kalendar", icon: <FaCalendarAlt /> },
        { path: "/profail", name: "Profail", icon: <FaMosque /> },
        { path: "/etv", name: "Etv", icon: <FaTv /> },
    ];

    const desktopMenuItems = [
        ...commonMenuItems,
        { path: "/mesej", name: "Mesej", icon: <FaComment /> },
        { path: "/asatizah", name: "Asatizah", icon: <FaUsers /> },
        { path: "/kitab", name: "Kitab", icon: <FaBook /> },

        kempenMode ? { path: "/kempen", name: "Kempen", icon: <FaHandHoldingHeart /> } : null,
    ].filter(Boolean);

    const mobileBottomItems = commonMenuItems;

    const mobileMoreItems = [
        { path: "/mesej", name: "Mesej", icon: <FaComment /> },
        { path: "/asatizah", name: "Asatizah", icon: <FaUsers /> },
        { path: "/kitab", name: "Kitab", icon: <FaBook /> },
        kempenMode ? { path: "/kempen", name: "Kempen", icon: <FaHandHoldingHeart /> } : null,
    ].filter(Boolean);


    const handleLogoutClick = () => setShowLogoutPopup(true);
    const cancelLogout = () => setShowLogoutPopup(false);
    const toggleMenu = () => setShowMenu(v => !v);
    const closeMenu = () => setShowMenu(false);

    if (isMobile) {
        return (
            <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
                <MobileBar />
                <main style={{ flex: 1, overflow: "auto", paddingBottom: "50px", position: "relative" }}>
                    {children}
                    {showMenu && (
                        <div
                            onClick={toggleMenu}
                            style={{
                                height: "auto",
                                position: "absolute",
                                top: 0, left: 0, right: 0, bottom: "80px",
                                zIndex: 9998,
                                pointerEvents: "auto",
                                backgroundColor: 'rgba(0, 0, 0, 0.5)'
                            }}
                        />
                    )}
                </main>
                <div className="bottom-navbar">
                    {mobileBottomItems.map((item, index) => (
                        <NavLink
                            onClick={closeMenu}
                            key={index}
                            to={item.path}
                            className={({ isActive }) =>
                                `bottom-navbar-link ${isActive && !showMenu ? 'active' : ''} ${isActive && showMenu ? 'inactive' : ''}`
                            }
                            style={{ textDecoration: "none" }}
                        >
                            <div className="nav-item">
                                <div className="icon">{item.icon}</div>
                                <div className="link_text">{item.name}</div>
                            </div>
                        </NavLink>
                    ))}

                    <div
                        onClick={toggleMenu}
                        className={`bottom-navbar-link ${showMenu ? 'active' : ''}`}
                        style={{ cursor: "pointer" }}
                    >
                        <div className="nav-item">
                            <div className="icon">{showMenu ? <FaTimes /> : <FaBars />}</div>
                            <div className="link_text">Lagi</div>
                        </div>
                    </div>
                </div>
                {showMenu && (
                    <div className="menu-container">
                        {mobileMoreItems.map((item, idx) => (
                            <NavLink
                                key={idx}
                                to={item.path}
                                onClick={closeMenu}   // ✅ tutup menu selepas klik
                                className="menu-item"
                            >
                                <span className="menu-icon">{item.icon}</span>
                                <span style={{ fontSize: "12px" }}>{item.name}</span>
                            </NavLink>
                        ))}

                        <div className="menu-item" onClick={onLogout}>
                            <FaSignOutAlt className="menu-icon" />
                            <span style={{ fontSize: "12px" }}>Logout</span>
                        </div>
                    </div>
                )}

            </div>
        );
    }

    return (
        <div style={{ display: "flex" }}>
            <div style={{ width: isOpen ? "180px" : "60px" }} className="sidebar">
                <div>
                    <div className="top_section">
                        <div style={{ display: isOpen ? "block" : "none" }}>
                            <div className="etikaf-container">
                                <img
                                    src="/images/etikaf.svg"
                                    className="etikaf-sidebar"
                                    alt="Etikaf"
                                    onClick={handleToggle}
                                />
                                <span className="badge">admin</span>
                            </div>
                        </div>
                        <div style={{ display: isOpen ? "none" : "block" }}>
                            <img
                                src="/images/e.svg"
                                style={{ width: "30px" }}
                                alt="e"
                                onClick={handleToggle}
                            />
                        </div>
                    </div>
                    {desktopMenuItems.map((item, index) => (
                        <NavLink
                            key={index}
                            to={item.path}
                            className={({ isActive }) => (isActive ? 'link active' : 'link')}
                            style={{ textDecoration: 'none' }}
                        >
                            <div className="icon">{item.icon}</div>
                            <div style={{ display: isOpen ? "block" : "none" }} className="link_text">{item.name}</div>
                        </NavLink>
                    ))}
                </div>
                <div className="link" onClick={handleLogoutClick}>
                    <div className="icon"><FaSignOutAlt /></div>
                    <div style={{ display: isOpen ? "block" : "none" }} className="link_text">Keluar</div>
                </div>
            </div>
            <main style={{ marginLeft: isOpen ? "180px" : "60px" }}>
                {children}
            </main>
            <Modal isOpen={showLogoutPopup} closeModal={cancelLogout} width="270px">
                <p style={{ color: "#000", fontSize: "16px", paddingBottom: "10px" }}>
                    <strong>Pasti untuk keluar?</strong>
                </p>
                <div className="modal-footer">
                    <span className="kembali" onClick={cancelLogout} style={{ marginLeft: "20px" }}>Kembali</span>
                    <button className="button" onClick={onLogout} style={{ marginLeft: "40px" }}>OK</button>
                </div>
            </Modal>
        </div>
    );
}

export default Sidebar;
